import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SamFilterType } from "../filterTypes";
import { AddFilterAction, SetFiltersAction, SetSearchFiltersAction, withFilterState } from "../utils/withFilterState";
import { GeometryFilter, GeometryFilterState, GeometrySelection } from "./geometryTypes";

export const samGeometrySliceName = "samGeometry";

export interface SamGeometryState {
  filters: GeometryFilterState;
  selection: GeometrySelection;
  selectionRequest?: string[];
}

const initialState: SamGeometryState = {
  filters: {},
  selection: {
    geometryEntryIds: [],
    additionalKeys: [],
  },
};

const samSlice = createSlice({
  name: samGeometrySliceName,
  initialState,
  reducers: {
    setGeometrySelection(state: SamGeometryState, action: PayloadAction<GeometrySelection>) {
      state.selection = action.payload;
    },
    setGeometryFilters(state: SamGeometryState, action: PayloadAction<SetFiltersAction<GeometryFilter>>) {
      withFilterState(state.filters).setFilters(action.payload);
    },
    setGeometrySearchFilters(state: SamGeometryState, action: PayloadAction<SetSearchFiltersAction>) {
      withFilterState(state.filters).setSearchFilters(action.payload);
    },
    addGeometryExcludeFilter(state: SamGeometryState, action: PayloadAction<AddFilterAction<GeometryFilter>>) {
      withFilterState(state.filters).addExcludeFilter(action.payload);
    },
    addGeometryIncludeFilter(state: SamGeometryState, action: PayloadAction<AddFilterAction<GeometryFilter>>) {
      withFilterState(state.filters).addIncludeFilter(action.payload);
    },
    invertGeometryFilters(state: SamGeometryState, action: PayloadAction<SamFilterType>) {
      withFilterState(state.filters).invertFilters(action.payload);
    },
    clearGeometryFilters(state: SamGeometryState, action: PayloadAction<SamFilterType>) {
      withFilterState(state.filters).clearFilters(action.payload);
    },
    setGeometrySelectionRequest(state: SamGeometryState, action: PayloadAction<string[] | undefined>) {
      state.selectionRequest = action.payload;
    },
  },
});

export interface SamGeometryRootState {
  [samGeometrySliceName]: SamGeometryState;
}

export const selectSamGeometry = (state: SamGeometryRootState) => state[samGeometrySliceName];
export const selectSamGeometrySelection = (state: SamGeometryRootState) => {
  return selectSamGeometry(state).selection;
};
export const selectSamGeometryFilters = (state: SamGeometryRootState) => {
  return selectSamGeometry(state).filters;
};
export const selectSamGeometrySelectionRequest = (state: SamGeometryRootState) => {
  return selectSamGeometry(state).selectionRequest;
};

export const {
  setGeometrySelection,
  setGeometryFilters,
  setGeometrySearchFilters,
  addGeometryExcludeFilter,
  addGeometryIncludeFilter,
  invertGeometryFilters,
  clearGeometryFilters,
  setGeometrySelectionRequest,
} = samSlice.actions;
export const samGeometryReducer = samSlice.reducer;
